import React, { useEffect, useState } from 'react';
import { useSpAccount } from '../SpAccountContext';
import SpOrders from './SpOrders';
import SpInventory from './SpInventory';
import SpBusinessReports from './SpBusinessReports';
import SpSyncHistory from './SpSyncHistory';
import SpScheduler from './SpScheduler';
import { useAuth } from '../AuthContext';

type Tab = 'orders' | 'inventory' | 'reports' | 'history' | 'scheduler';

const TABS: { id: Tab; label: string }[] = [
  { id: 'orders', label: 'Orders' },
  { id: 'inventory', label: 'Inventory' },
  { id: 'reports', label: 'Business Reports' },
  { id: 'history', label: 'Sync History' },
  { id: 'scheduler', label: 'Scheduler' },
];

// A tab is shown only if the user holds at least one of its permissions (admins
// hold all). Sync History is cross-cutting, so any data-view permission unlocks it.
const TAB_PERMS: Record<Tab, string[]> = {
  orders: ['orders.view'],
  inventory: ['inventory.view'],
  reports: ['business_reports.view'],
  history: ['orders.view', 'inventory.view', 'business_reports.view'],
  scheduler: ['scheduler.view'],
};

// Each tab gets its own URL so a refresh / shared link lands on the same tab.
// Orders lives at the bare /amazon-reports path; the rest are sub-paths. The
// top-level router (App.tsx) resolves any /amazon-reports* path to this page.
const TAB_PATHS: Record<Tab, string> = {
  orders: '/amazon-reports/orders',
  inventory: '/amazon-reports/inventory',
  reports: '/amazon-reports/business-reports',
  history: '/amazon-reports/sync-history',
  scheduler: '/amazon-reports/scheduler',
};
const PATH_TO_TAB: Record<string, Tab> = {
  '/amazon-reports': 'orders',
  ...Object.fromEntries(Object.entries(TAB_PATHS).map(([t, p]) => [p, t as Tab])),
} as Record<string, Tab>;

function tabFromUrl(): Tab {
  return PATH_TO_TAB[window.location.pathname] ?? 'orders';
}

const AmazonReports: React.FC = () => {
  const { accounts, activeAccount, activeMarketplaceId, setActiveAccount, setActiveMarketplaceId } = useSpAccount();
  const { hasPermission } = useAuth();
  const [tab, setTab] = useState<Tab>(tabFromUrl);

  // Push a distinct URL per tab so refresh restores it; keep tab in sync with
  // browser back/forward.
  const selectTab = (id: Tab) => {
    setTab(id);
    if (window.location.pathname !== TAB_PATHS[id]) {
      window.history.pushState({}, '', TAB_PATHS[id]);
    }
  };

  useEffect(() => {
    const onPop = () => setTab(tabFromUrl());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Normalize the bare /amazon-reports URL to the orders sub-path on first load
  // so the address bar always reflects the visible tab.
  useEffect(() => {
    if (window.location.pathname === '/amazon-reports') {
      window.history.replaceState({}, '', TAB_PATHS[tab]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only show tabs the user is permitted to view (admins hold every permission).
  const visibleTabs = TABS.filter(t => TAB_PERMS[t.id].some(p => hasPermission(p)));

  // If the current/URL tab isn't permitted, fall back to the first allowed tab.
  useEffect(() => {
    if (visibleTabs.length && !visibleTabs.some(t => t.id === tab)) {
      selectTab(visibleTabs[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTabs, tab]);

  const activeTab: Tab | null = visibleTabs.some(t => t.id === tab)
    ? tab
    : (visibleTabs[0]?.id ?? null);

  if (!activeAccount) {
    return (
      <div>
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📦</div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>No Amazon account selected</div>
          <div style={{ color: 'var(--text-muted)' }}>
            Go to <strong>Amazon Accounts</strong> in the sidebar to connect your first account.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Tabs row with account selectors on the right */}
      <div style={{ display: 'flex', alignItems: 'center', borderBottom: '2px solid var(--border)', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 0, flex: 1 }}>
          {visibleTabs.map(t => (
            <button
              key={t.id}
              onClick={() => selectTab(t.id)}
              style={{
                padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer',
                fontWeight: activeTab === t.id ? 700 : 500, fontSize: 14,
                color: activeTab === t.id ? 'var(--accent)' : 'var(--text-muted)',
                borderBottom: activeTab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
                marginBottom: -2, transition: 'all 0.15s',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
        {/* Account / marketplace selectors */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 6 }}>
          <select
            className="form-input"
            style={{ fontSize: 12, padding: '4px 8px', width: 180 }}
            value={activeAccount.seller_id}
            onChange={e => {
              const acc = accounts.find(a => a.seller_id === e.target.value);
              if (acc) { setActiveAccount(acc); setActiveMarketplaceId(null); }
            }}
          >
            {accounts.map(acc => (
              <option key={acc.seller_id} value={acc.seller_id}>
                {acc.account_name} ({acc.region})
              </option>
            ))}
          </select>
          <select
            className="form-input"
            style={{ fontSize: 12, padding: '4px 8px', width: 160 }}
            value={activeMarketplaceId ?? ''}
            onChange={e => setActiveMarketplaceId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">All Marketplaces</option>
            {activeAccount.marketplaces.map(m => (
              <option key={m.id} value={m.id}>{m.code} — {m.domain}</option>
            ))}
          </select>
        </div>
      </div>

      {activeTab === 'orders' && <SpOrders account={activeAccount} marketplaceId={activeMarketplaceId} />}
      {activeTab === 'inventory' && <SpInventory account={activeAccount} marketplaceId={activeMarketplaceId} />}
      {activeTab === 'reports' && <SpBusinessReports account={activeAccount} marketplaceId={activeMarketplaceId} />}
      {activeTab === 'history' && <SpSyncHistory account={activeAccount} />}
      {activeTab === 'scheduler' && <SpScheduler account={activeAccount} />}
      {!activeTab && (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>No access</div>
          <div style={{ color: 'var(--text-muted)' }}>
            You don't have permission to view any Amazon Data sections.
          </div>
        </div>
      )}
    </div>
  );
};

export default AmazonReports;
